import isEqual from 'lodash/isEqual';
import { filter, map } from 'rxjs';
import {
  trackBrowserHistoryNavigation,
  trackPageReloadIfApplicable,
} from 'router/browserNavigationTracking';
import { createPath, type History, type Location } from 'router/history';
import {
  locationSearchParams,
  locationToPathname,
} from './declarativeRouterLogic';
import type { DeclarativeRouter } from './declarativeRouter';
import {
  isUnmatchedLocation,
  type ArrivedLocation,
  type UnmatchedLocation,
} from './location';
import type { Params } from './paramRegistry';
import type { RouteNode } from './routeNode';

export type { ArrivedLocation };

type BrowserHistoryEntry = {
  path: string;
  state: Partial<Params>;
  action: 'push' | 'replace';
};

type BrowserRouterAdapterOptions<RouteTree extends RouteNode> = {
  router: DeclarativeRouter<RouteTree>;
  browserHistory: History;
  onArrive: (location: ArrivedLocation<RouteTree>) => Promise<void> | void;
  onNavigationError: (error: unknown) => void;
};

export type BrowserRouterAdapter = {
  start: () => Promise<void>;
  teardown: VoidFunction;
};

const toHiddenParams = (state: unknown): Partial<Params> | undefined => {
  if (!state || typeof state !== 'object') return undefined;
  return state as Partial<Params>;
};

const isArrivedLocation = <RouteTree extends RouteNode>(
  location: ArrivedLocation<RouteTree> | UnmatchedLocation
): location is ArrivedLocation<RouteTree> => !isUnmatchedLocation(location);

const toBrowserHistoryEntry = <RouteTree extends RouteNode>(
  location: ArrivedLocation<RouteTree>,
  routeTree: RouteTree
): BrowserHistoryEntry => ({
  path: createPath({
    pathname: locationToPathname(location),
    search: locationSearchParams(routeTree, location).toString(),
    hash: location.hash ?? '',
  }),
  state: location.hiddenParams,
  action: location.action,
});

const writeHistoryEntry = (
  browserHistory: History,
  { path, state, action }: BrowserHistoryEntry
): void => {
  const isAlreadyThere =
    path === createPath(browserHistory.location) &&
    isEqual(state, toHiddenParams(browserHistory.location.state) ?? {});
  if (isAlreadyThere) return;

  browserHistory[action](path, state);
};

export const createBrowserRouterAdapter = <RouteTree extends RouteNode>({
  router,
  browserHistory,
  onArrive,
  onNavigationError,
}: BrowserRouterAdapterOptions<RouteTree>): BrowserRouterAdapter => {
  let disconnect: VoidFunction | null = null;

  const arrive = async (location: Location): Promise<void> => {
    const arrivedLocation = router.arrive(
      location.pathname,
      new URLSearchParams(location.search),
      toHiddenParams(location.state),
      location.hash || undefined,
      'replace'
    );

    if (!arrivedLocation) {
      throw new Error(`Invalid route: ${location.pathname}`);
    }

    await onArrive(arrivedLocation);
  };

  const teardown = (): void => {
    disconnect?.();
    disconnect = null;
  };

  const start = async (): Promise<void> => {
    teardown();
    trackPageReloadIfApplicable();

    const subscription = router
      .pipe(
        filter(isArrivedLocation),
        map(location => toBrowserHistoryEntry(location, router.routeTree))
      )
      .subscribe(entry => writeHistoryEntry(browserHistory, entry));

    const unlisten = browserHistory.listen(({ location, action }) => {
      if (action !== 'traverse') return;
      trackBrowserHistoryNavigation();
      arrive(location).catch(onNavigationError);
    });

    disconnect = () => {
      subscription.unsubscribe();
      unlisten();
    };

    try {
      await arrive(browserHistory.location);
    } catch (error) {
      teardown();
      throw error;
    }
  };

  return { start, teardown };
};
